import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import useApiManager from '../../../utils/Api/apiManager';
import { useNavigate, Link } from "react-router-dom";
import { LeadIndustry, LeadRating, LeadSource, LeadStatus } from '../../../utils/enums'

const ContactsDataGrid = ({ filters, selectedRows, onSelectionModelChange, unassigned, campaignId, triggerFetch, setTriggerFetch }) => {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sortModel, setSortModel] = useState([]);
    const navigate = useNavigate();
    const { post } = useApiManager();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 50,
    });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [filterParams, setFilterParams] = useState([]);

    const requestIdRef = useRef(0);

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5 },
        {
            field: 'firstName',
            headerName: 'First Name',
            flex: 1,
            renderCell: (params) => {
                const displayName = params.value?.trim() || "(N/A)"; // fallback if null, undefined, or empty
                return (
                    <Link
                        to={`/contacts/${params.row.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit' }}
                    >
                        {displayName}
                    </Link>
                );
            },
        },
        { field: 'lastName', headerName: 'Last Name', flex: 1 },
        { field: 'title', headerName: 'Title', flex: 1 },
        { field: 'company', headerName: 'Company', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1 },
        { field: 'phone', headerName: 'Phone', flex: 0.8 },
        { field: 'mobile', headerName: 'Mobile', flex: 0.8 },
        { field: 'leadSource', headerName: 'Lead Source', flex: 0.8 },
        { field: 'ownerName', headerName: 'Lead Owner', flex: 0.8 },
    ];

    const transformData = (data) => {
        return data.map(item => ({
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            title: item.title,
            company: item.company,
            email: item.email,
            phone: item.phone,
            mobile: item.mobile,
            leadSource: LeadSource?.[item.leadSource]?.label || '',
            ownerName: item.ownerName
        }));
    };

    useEffect(() => {
        if (filters == null) return;

        // Only fetch if filters are either empty (user cleared them) or valid
        const allEmpty = filters.length === 0 || filters.every(f => !f.fieldValue);
        if (!allEmpty || filters.length === 0) {
            setFilterParams(filters);
        }

    }, [filters]);

    useEffect(() => {
        const fetchAndSetData = async () => {
            setLoading(true);

            const currentRequestId = ++requestIdRef.current; // Track request order

            try {
                const operatorMapping = {
                    "contains": 2,
                    "equals": 0,
                    "starts with": 4,
                    "ends with": 5,
                    "is empty": 6,
                    "is not empty": 7,
                    // "is any of": Not directly mapped
                };



                const sortField = sortModel.length > 0 ? sortModel[0].field : 'createdAt'; // Default to 'firstName'
                const sortDescending = sortModel.length > 0 ? sortModel[0].sort === 'desc' : true;

                let url = "/contacts/paged";
                // gets contacts that do not have any account
                if (unassigned) {
                    url += "/account/unassigned";
                }

                if (campaignId) {
                    url += `/campaign/${campaignId}/unassigned`;
                }

                const data = await post(url, {
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    filters: filterParams,
                    sortField: sortField,
                    sortDescending: sortDescending
                });
                // Only update if this is the latest request
                if (currentRequestId === requestIdRef.current) {
                    setRows(transformData(data.items));
                    setRowCount(data.totalCount);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                if (currentRequestId === requestIdRef.current) {
                    setLoading(false);
                    if (setTriggerFetch) {
                        setTriggerFetch(false);
                    }
                }
            }
        };

        fetchAndSetData();
    }, [paginationModel, filterParams, sortModel, triggerFetch]);

    const handleRowClick = (params) => {
        navigate(`/contacts/${params.id}`);
    };

    return (
        <DataGrid
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            paginationMode="server"
            sortingMode="server"
            columnVisibilityModel={{
                id: false,
            }}
            getRowId={(row) => row.id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[50, 75, 100]}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            filterMode="server"
            onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
            }}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={onSelectionModelChange}
            disableRowSelectionOnClick
            loading={loading}
            onRowClick={handleRowClick}
            sx={{
                height: '100%', // Fills the parent container
                '& .MuiDataGrid-virtualScroller': {
                    overflow: 'auto', // Ensures scrollbars appear when necessary
                },
            }}
        />
    );
};

export default ContactsDataGrid;
